import { Rule } from '../models/rule-engine';

/** Default outcome phrasing for pre-validation (Ignore Order). */
const IGNORE_OUTCOME = 'Order ignored';

/** Map common XPath / field path endings to friendly labels used in messages. */
const FRIENDLY_FIELD_LABELS: Record<string, string> = {
  ordercode: 'Order Code',
  ordertype: 'Order Type',
  testcode: 'Test Code',
  instrumenttestcode: 'Test Code',
  machinetestcode: 'Test Code',
  labcode: 'Lab Code',
  locationcode: 'Lab Code',
  accountcode: 'Lab Code',
  clientnumber: 'Lab Code',
  testname: 'Test Name',
  accessionnumber: 'Accession Number',
  requisitionnumber: 'Requisition Number',
};

/**
 * Derive a short human-readable field label from an XPath or dotted path.
 */
export function getFriendlyFieldLabel(field: string | undefined | null): string {
  if (!field || !String(field).trim()) {
    return 'Field';
  }
  const raw = String(field).trim();

  // Already a friendly label like "Case.Order Code"
  if (!raw.includes('/') && !raw.includes('@') && raw.includes('.')) {
    const afterDot = raw.split('.').pop()?.trim();
    if (afterDot) {
      return afterDot;
    }
  }

  const attrMatch = raw.match(/@([A-Za-z0-9_]+)\s*(?:\]|$)/);
  const token = attrMatch?.[1] ?? raw.split('/').filter(Boolean).pop() ?? raw;
  const cleaned = token.replace(/[\[\]@]/g, '').trim();
  const key = cleaned.toLowerCase();
  if (FRIENDLY_FIELD_LABELS[key]) {
    return FRIENDLY_FIELD_LABELS[key];
  }
  // PascalCase / camelCase → spaced words
  return cleaned
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .trim() || 'Field';
}

function isNegativeOperator(operator: string | undefined | null): boolean {
  const op = (operator ?? '').toString().trim().toLowerCase().replace(/\s+/g, '');
  return (
    op === 'notin' ||
    op === 'notequals' ||
    op === 'ne' ||
    op === '!=' ||
    op === '<>' ||
    op === 'doesnotcontain' ||
    op === 'notcontains'
  );
}

function formatRuleValues(value: string | undefined | null): string {
  if (value == null || String(value).trim() === '') {
    return '';
  }
  const parts = String(value)
    .split('|')
    .map((v) => v.trim())
    .filter((v) => v.length > 0);
  if (parts.length === 0) {
    return '';
  }
  if (parts.length === 1) {
    return `"${parts[0]}"`;
  }
  if (parts.length === 2) {
    return `"${parts[0]}" or "${parts[1]}"`;
  }
  const head = parts
    .slice(0, -1)
    .map((p) => `"${p}"`)
    .join(', ');
  return `${head}, or "${parts[parts.length - 1]}"`;
}

function describeRuleCondition(rule: Rule): { text: string; isNegative: boolean } {
  const label = getFriendlyFieldLabel(rule.field);
  const negative = isNegativeOperator(rule.operator);
  const values = formatRuleValues(rule.value);

  if (negative) {
    return {
      text: values ? `${label} ${values}` : label,
      isNegative: true,
    };
  }
  return {
    text: values ? `${label} ${values}` : label,
    isNegative: false,
  };
}

function joinWithAnd(parts: string[]): string {
  if (parts.length === 0) {
    return '';
  }
  if (parts.length === 1) {
    return parts[0];
  }
  if (parts.length === 2) {
    return `${parts[0]} and ${parts[1]}`;
  }
  return `${parts.slice(0, -1).join(', ')}, and ${parts[parts.length - 1]}`;
}

/**
 * Build a default Case Audit / validation message from conditions.
 * Always uses Ignore Order phrasing. Users can edit afterward.
 */
export function generatePreValidationMessage(rules: Rule[]): string {
  if (!rules?.length) {
    return '';
  }

  const described = rules.map(describeRuleCondition);
  const allNegative = described.every((d) => d.isNegative);
  const allPositive = described.every((d) => !d.isNegative);

  if (allNegative) {
    const fieldLabels = joinWithAnd(
      rules.map((r) => getFriendlyFieldLabel(r.field))
    );
    return `${IGNORE_OUTCOME} because ${fieldLabels} did not meet the configured pre-validation criteria.`;
  }

  if (allPositive) {
    const matched = joinWithAnd(described.map((d) => d.text));
    return `${IGNORE_OUTCOME} because ${matched} matched the configured pre-validation rule.`;
  }

  // Mixed positive / negative operators
  const matchedParts = described
    .filter((d) => !d.isNegative)
    .map((d) => d.text);
  const unmetLabels = rules
    .filter((r) => isNegativeOperator(r.operator))
    .map((r) => getFriendlyFieldLabel(r.field));
  const chunks: string[] = [];
  if (matchedParts.length) {
    chunks.push(`${joinWithAnd(matchedParts)} matched`);
  }
  if (unmetLabels.length) {
    chunks.push(
      `${joinWithAnd(unmetLabels)} did not meet the configured criteria`
    );
  }
  return `${IGNORE_OUTCOME} because ${chunks.join(' and ')} for the pre-validation rule.`;
}
